import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  MY_EXPERT_DOCUMENTS_QUERY,
  UPLOAD_EXPERT_DOCUMENT_MUTATION,
  DELETE_EXPERT_DOCUMENT_MUTATION,
} from '../graphql';

const DOC_TYPES = [
  { value: 'IDENTITY',      label: '🪪 Identity (Passport / Driving Licence)' },
  { value: 'DBS_CHECK',     label: '🔍 DBS / Background Check' },
  { value: 'TEACHING_CERT', label: '🎓 Teaching Certification' },
  { value: 'FIRST_AID',     label: '🩺 First Aid Certificate' },
  { value: 'INSURANCE',     label: '🛡️ Insurance / Indemnity' },
  { value: 'OTHER',         label: '📄 Other' },
];

const STATUS_STYLE = {
  PENDING_REVIEW: { bg: 'rgba(245,158,11,0.12)', color: '#f59e0b', label: '⏳ Pending Review' },
  VERIFIED:       { bg: 'rgba(16,185,129,0.12)',  color: '#10b981', label: '✅ Verified' },
  REJECTED:       { bg: 'rgba(239,68,68,0.12)',   color: '#ef4444', label: '❌ Rejected' },
};

export default function ExpertDocuments() {
  const { data, loading, refetch } = useQuery(MY_EXPERT_DOCUMENTS_QUERY);
  const [upload, { loading: uploading }] = useMutation(UPLOAD_EXPERT_DOCUMENT_MUTATION, { onCompleted: () => { refetch(); setForm({ docType: 'IDENTITY', fileName: '', fileUrl: '', notes: '' }); setShowForm(false); } });
  const [remove, { loading: removing }] = useMutation(DELETE_EXPERT_DOCUMENT_MUTATION, { onCompleted: refetch });

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ docType: 'IDENTITY', fileName: '', fileUrl: '', notes: '' });
  const [err, setErr] = useState('');

  const docs = data?.myExpertDocuments || [];

  function handleSubmit(e) {
    e.preventDefault();
    setErr('');
    if (!form.fileName.trim()) return setErr('File name is required');
    if (!form.fileUrl.trim()) return setErr('Document link is required');
    try { new URL(form.fileUrl); } catch { return setErr('Enter a valid URL (e.g. Google Drive / Dropbox link)'); }
    upload({ variables: { docType: form.docType, fileName: form.fileName.trim(), fileUrl: form.fileUrl.trim(), notes: form.notes.trim() } });
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 'var(--font-lg)' }}>Verified Documents</div>
          <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 2 }}>
            Upload credentials for background verification. Groups can see your verification status.
          </div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowForm(v => !v)}>
          {showForm ? 'Cancel' : '+ Upload Document'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px', marginBottom: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Document Type</label>
              <select className="input" value={form.docType} onChange={e => setForm(f => ({ ...f, docType: e.target.value }))}>
                {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>File Name</label>
              <input className="input" placeholder="e.g. passport_scan.pdf" value={form.fileName} onChange={e => setForm(f => ({ ...f, fileName: e.target.value }))} />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Document Link (Google Drive / Dropbox / Box)</label>
            <input className="input" placeholder="https://drive.google.com/..." value={form.fileUrl} onChange={e => setForm(f => ({ ...f, fileUrl: e.target.value }))} />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Paste a shareable link. Only admins can access it.</div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Notes (optional)</label>
            <input className="input" placeholder="e.g. Valid until Dec 2027" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          {err && <div style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 10 }}>{err}</div>}
          <button className="btn btn-primary" type="submit" disabled={uploading}>{uploading ? 'Uploading…' : 'Submit for Verification'}</button>
        </form>
      )}

      {loading && <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</div>}

      {!loading && docs.length === 0 && (
        <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>📋</div>
          <div style={{ fontWeight: 600 }}>No documents uploaded yet</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Upload your credentials to unlock verified status on groups</div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {docs.map(doc => {
          const st = STATUS_STYLE[doc.status] || STATUS_STYLE.PENDING_REVIEW;
          const typeLabel = DOC_TYPES.find(t => t.value === doc.docType)?.label || doc.docType;
          return (
            <div key={doc.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                  <span style={{ fontWeight: 700, fontSize: 'var(--font-sm)' }}>{typeLabel}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 99, background: st.bg, color: st.color }}>{st.label}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{doc.fileName}</div>
                {doc.notes && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, fontStyle: 'italic' }}>"{doc.notes}"</div>}
                {doc.reviewNote && doc.status === 'REJECTED' && (
                  <div style={{ fontSize: 12, color: '#ef4444', marginTop: 4, background: 'rgba(239,68,68,0.08)', borderRadius: 6, padding: '4px 8px' }}>
                    Feedback: {doc.reviewNote}
                  </div>
                )}
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                  Uploaded {new Date(doc.uploadedAt).toLocaleDateString()}
                  {doc.reviewedAt && ` · Reviewed ${new Date(doc.reviewedAt).toLocaleDateString()}`}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <a href={doc.fileUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">View</a>
                {doc.status !== 'VERIFIED' && (
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }}
                    disabled={removing}
                    onClick={() => { if (window.confirm('Delete this document?')) remove({ variables: { docId: doc.id } }); }}>
                    Delete
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
